// DataTable — the admin-preset data grid per COMPONENTS §Catalog + §States.
//
// A data-grid, not a styled <table>: a HEADLESS engine (TanStack Table v9,
// ADR-027) owns the logic (sort, client-side global filter, group bands, tree
// expansion; pinning / virtualization reachable later) while we own 100% of
// the markup — tokens, dark mode, i18n, a11y. Generic over the row type <T>;
// a STABLE getRowId is required (dev's array-index identity broke on sort —
// a footgun this design closes).
//
// TWO band sources, ONE band markup (ADR-027 · ADR-078 §4): the CLIENT bands
// (`groupBy` — the engine's grouped row model over a load-all list) and the
// SERVER-fed bands (`serverBands` — the server's band list with its true
// counts and the STREAM page: the opened bands' rows in band order, each row
// stamped with its band; this component is a pure render of that answer —
// it computes no band, it lays the headers between the runs).
//
// First home of the 5-STATE CONTRACT (COMPONENTS §States), driven off the
// TanStack Query signals the caller passes:
//   loading    isPending && !data        → header + Skeleton rows
//   refreshing isFetching && data         → content stays + a brand pulse strip
//   empty      data && rows.length === 0  → the `empty` slot (or a default)
//   error      isError && !data           → <Alert> + optional retry
//   populated  rows.length > 0            → the sorted/filtered rows
// With no query signals it just renders the array it's given (static data).
//
// NOT here (deliberate, per the phase scope): pagination (compose the sibling
// <Pagination>); column-chooser UI + sticky column (APP-017); card-view render
// mode (alternate renderers — ADR-037); row virtualization (monitoring). The
// filter INPUT is page-owned (the list page owns the filter bar) — this only
// accepts a controlled `globalFilter` value.

import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode, type UIEvent } from 'react';
import {
  useTable,
  tableFeatures,
  rowSortingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  rowExpandingFeature,
  columnGroupingFeature,
  createSortedRowModel,
  createFilteredRowModel,
  createGroupedRowModel,
  createExpandedRowModel,
  type ColumnDef,
  type RowData,
  type SortFn,
  type SortingState,
  type OnChangeFn,
  type ExpandedState,
} from '@tanstack/react-table';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.js';
import { Alert } from './Alert.js';
import { EmptyState } from './EmptyState.js';
import { Skeleton } from './Skeleton.js';
import { useStickyTopPx } from '../../lib/shellTop.js';
import { formatNumber } from '../../lib/format.js';

// The engine's features — registered once; the engine carries only what is
// registered. Sorting · the global filter (its row model belongs to the
// column-filtering feature) · expansion (tree mode and group bands) ·
// grouping. A flat table runs through the grouped and expanded models
// unchanged: no grouping state, nothing expanded.
const features = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  globalFilteringFeature,
  rowExpandingFeature,
  columnGroupingFeature,
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  groupedRowModel: createGroupedRowModel(),
  expandedRowModel: createExpandedRowModel(),
});
type Features = typeof features;

export interface DataTableColumn<T> {
  /** Stable column id; also the sort key. */
  id: string;
  /** Header content (caller-supplied, i18n'd). */
  header: ReactNode;
  /** Plain-text column name for the View gear's column toggles + a11y (`header` may be JSX). Falls back to the id. */
  label?: string;
  /** Cell renderer for a row. */
  cell: (row: T) => ReactNode;
  /** Enable client-side sorting + global-filter matching for this column. */
  sortable?: boolean;
  /** Value used for sorting + filtering. Required for a sortable column.
   *  Blank values (null / undefined / '') sort LAST in both directions —
   *  the engine maps them to `sortUndefined: 'last'`. */
  value?: (row: T) => string | number | Date | null | undefined;
  /** First header click sorts descending (dates newest-first, amounts
   *  biggest-first). Explicit beats TanStack's value-type inference — set it
   *  wherever the useful first direction isn't A→Z, ESPECIALLY under
   *  `manualSorting` (the server can't be re-inferred from). */
  sortDescFirst?: boolean;
  /** Raw group key for this column when it is the `groupBy` target (e.g. the
   *  cluster's id, not its display label). Falls back to `value`. The BAND
   *  content (label + count + aggregates) is the table's `groupHeader`. */
  groupValue?: (row: T) => string;
  align?: 'start' | 'end' | 'center';
  /** Fixed column width (CSS, e.g. '8rem'). */
  width?: string;
  /** CSS min-width applied to the column (explicit only — no default is
   *  forced, so a cell whose responsive faces shrink below the fit floor on
   *  the phone isn't CSS-pinned wide there). Also the fit floor's fallback
   *  when `floor` is absent. Growth stays CSS `table-layout: auto` (surplus
   *  distributes by content; `width` is the preferred-width cap) —
   *  deliberately no flex factors. */
  minWidth?: string;
  /** WIDTH INTENT — the FIT FLOOR: the border-box width below which this
   *  column stops being readable at the widths where fit runs (CSS length,
   *  `'Nrem'`/`'Npx'`). Consumed by `computeColumnFit` (Σ floors against the
   *  measured container decides what fits); falls back to `minWidth`, then
   *  `COLUMN_FLOOR_DEFAULT`; `COLUMN_FLOORS` carries per-kind suggestions.
   *  Declare `floor` alone (no `minWidth`) when the cell's own responsive
   *  faces shrink below it on the phone — the math stays honest at md+ while
   *  CSS never pins the phone face wide. Calibrate as a COMFORTABLE minimum —
   *  the floor is where the taste lives; an understated floor is surfaced by
   *  the dev overflow warning. */
  floor?: string;
  /** YIELD RANK — when the floors don't fit the measured container, columns
   *  drop ONE AT A TIME in ascending rank until they do, and return the moment
   *  there's room. Explicit ranks stay < 10 000; unset → the column yields
   *  after every explicitly-ranked one, later-in-DOM first. The FIRST column
   *  (the identity anchor) never drops, ever. Dropped data stays reachable on
   *  the row's peek / detail page. Distinct from the View gear (a user display
   *  pref): this is the AUTHOR's importance ranking, sizeless — never a
   *  breakpoint. */
  dropOrder?: number;
  /** This column never leaves the table (row actions; the Observations
   *  anchor). On a too-tight box it DEGRADES to its `degraded` face instead —
   *  or, without one, holds its floor and truncates (the dev warning polices).
   *  The identity column (index 0) is never-drop implicitly. */
  neverDrop?: boolean;
  /** The compact face a never-drop column swaps to when the box is too tight:
   *  a tighter floor + an alternate render (e.g. glyph-only marks). */
  degraded?: { minWidth: string; header?: ReactNode; cell: (row: T) => ReactNode };
  /** Rank of the degrade step on the SAME yield ladder as drops. Unset →
   *  degrades run after ALL drops (last resort); an explicit rank (< 10 000)
   *  interleaves — e.g. degrade the anchor before dropping the last data
   *  column. */
  degradeOrder?: number;
  /** Mark the hierarchy column of an expandable tree-table (paired with `getSubRows`):
   *  DataTable renders the depth indent + an accessible expand/collapse disclosure
   *  (a real `aria-expanded` button) before the cell. No effect on a flat table. */
  tree?: boolean;
  /** A GROUPING-ONLY column: carries a `groupValue` for the `groupBy` band key but
   *  renders NO header/cell (kept out of the visible column flow + the band colspan).
   *  Lets a list group by a LOGICAL axis that has no standalone column — e.g. a
   *  priority folded into the identity chip, or a zone shown as a cluster sub-line. */
  groupingOnly?: boolean;
}

/** A client-banded list's act on all its bands at once, as the page hands it (`bandsCommand`). */
export interface BandsCommand {
  act: 'open' | 'fold';
  /** Changes at every press — the same act asked twice is two commands. */
  n: number;
}

/** One band of a SERVER-fed grouped view — the server's own words (ADR-078 §4). */
export interface ServerBand {
  key: string;
  /** The band's name when the server holds it; null = the surface words the key (a ladder word, "not set"). */
  label: string | null;
  /** The band's rows under the list's filters — exact, or a FLOOR when `capped` (printed "n+"). */
  count: number;
  capped: boolean;
}

/** The server-fed band mode: a PURE RENDER of a grouped answer. The bands are
 *  the whole population's, in the axis's order; `data` is the STREAM's page —
 *  the opened bands' rows in band order then the sort, each stamped with its
 *  band (`bandOf`). A folded band is one header line; an opened band's header
 *  sits above its run, pins under the thead while you are inside it, reads
 *  "continued" when the page starts inside the band, and says where you are
 *  ("rows 101–200 of 300") when the band spans pages. No aggregates (the chips
 *  are the breakdown), no arrows (the folded headers are the index; the pager
 *  does the rest). */
export interface ServerBandsProps<T> {
  bands: readonly ServerBand[];
  /** The opened keys — or every band (`'*'`). Absent keys are folded (how a grouped view arrives). */
  open: ReadonlySet<string> | '*';
  /** The row's band key — the server's stamp. */
  bandOf: (row: T) => string;
  /** The person opens or folds a band (the page writes the URL — replace, never a history entry). */
  onToggle: (key: string, open: boolean) => void;
  /** The band's words — a ladder word printed by key, a name as given, a glyph beside it: the surface's slot. */
  renderLabel: (band: ServerBand) => ReactNode;
  /** The stream page `data` holds: its offset and the page size (the headers' positions and "continued"). */
  page: { offset: number; size: number };
}

/** How the stream's page lays out: the bands' headers and the rows' runs, in PAGE order. A header's `id` is the
 *  row's React key — `band:<key>`, and `band:<key>:<n>` for a band's later run on the same page. */
export type StreamSegment =
  | { kind: 'band'; id: string; band: ServerBand; open: boolean; continued: boolean; position: { from: number; to: number } | null }
  | { kind: 'rows'; from: number; to: number };

/** The stream page's layout (pure — unit-tested). The RUNS come from the rows'
 *  own stamps, in PAGE order: the server's order is the stream's, every row is
 *  drawn, a key that comes round again starts another run (drawn again,
 *  "continued"). The HEADERS sit where their position in the stream falls —
 *  the sum of the opened bands' counts before them: a band with rows on this
 *  page above its first run; a band without — folded, or opened with its rows
 *  on another page (drawn expanded and empty, so it keeps its place and its
 *  toggle) — between the runs at its position, or after the last run of the
 *  LAST page (the tail). The last page is known from the counts (the page
 *  reaches the stream's end) or from a short page — never from a full page
 *  alone, which would hide the tail behind a page the pager cannot offer; an
 *  empty page past the end draws every header, so the person can move.
 *  "continued" and "rows x–y of n" read the same positions — exact: the server
 *  bounds the stream to the rows it counted, so the counts ARE the stream's
 *  rows under a floor as well; no position on a page whose runs interleave
 *  (the counts' arithmetic does not hold there). */
export function streamSegments(bands: readonly ServerBand[], isOpen: (key: string) => boolean, rowBands: readonly string[], page: { offset: number; size: number }): StreamSegment[] {
  const n = rowBands.length;
  const runs: { key: string; from: number; to: number; ord: number }[] = [];
  const ords = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    const key = rowBands[i]!;
    const run = runs[runs.length - 1];
    if (run && run.key === key) run.to = i + 1;
    else {
      const ord = ords.get(key) ?? 0;
      ords.set(key, ord + 1);
      runs.push({ key, from: i, to: i + 1, ord });
    }
  }
  const interleaved = runs.some((r) => r.ord > 0);
  // Where each band starts in the stream (the server bounds the stream to the rows it counted, so a position is exact
  // under a floor as well); the sum is the stream's known end.
  const starts = new Map<string, { at: number }>();
  let pos = 0;
  for (const b of bands) {
    starts.set(b.key, { at: pos });
    if (isOpen(b.key)) pos += b.count;
  }
  const last = n < page.size || page.offset + n >= pos;
  const past = n === 0 && page.offset > 0;
  const byKey = new Map(bands.map((b) => [b.key, b]));
  const out: StreamSegment[] = [];
  const drawn = new Set<string>();
  // The bands without a run on this page whose position falls up to `limit` (a stream position), in band order.
  const headersUpTo = (limit: number) => {
    for (const b of bands) {
      if (drawn.has(b.key) || ords.has(b.key)) continue;
      const at = starts.get(b.key)!.at;
      if (!past && !(at >= page.offset && at <= limit)) continue;
      drawn.add(b.key);
      out.push({ kind: 'band', id: `band:${b.key}`, band: b, open: isOpen(b.key), continued: false, position: null });
    }
  };
  for (const run of runs) {
    headersUpTo(page.offset + run.from);
    const len = run.to - run.from;
    const b = byKey.get(run.key);
    const start = starts.get(run.key);
    let continued = false;
    let position: { from: number; to: number } | null = null;
    if (b && start) {
      if (run.ord > 0 || interleaved) continued = run.ord > 0 || (run.from === 0 && start.at < page.offset);
      else if (run.from > 0) position = len >= b.count ? null : { from: 1, to: len };
      else {
        continued = start.at < page.offset;
        const from = page.offset - start.at + 1;
        // A band list ordered against the rows would print a position below one — never printed (unreachable from the server).
        position = from < 1 || (from === 1 && len >= b.count) ? null : { from, to: from + len - 1 };
      }
    }
    drawn.add(run.key);
    out.push({
      kind: 'band',
      id: run.ord ? `band:${run.key}:${run.ord}` : `band:${run.key}`,
      // A run whose band the list does not know (a race between the answer's two halves): shown under its key, never dropped.
      band: b ?? { key: run.key, label: null, count: len, capped: false },
      open: true,
      continued,
      position,
    });
    out.push({ kind: 'rows', from: run.from, to: run.to });
  }
  if (last) headersUpTo(Infinity);
  return out;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[] | undefined;
  /** Stable row identity — required. */
  getRowId: (row: T) => string;
  /** Screen-reader accessible name for the table (sr-only <caption>). */
  caption: string;
  // 5-state signals (TanStack Query). Omit for static data.
  isPending?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  /** Empty-state content (caller passes entity copy + optional gated CTA). */
  empty?: ReactNode;
  /** Error-state message (defaults to a generic one). */
  errorMessage?: string;
  /** Show a Retry action in the error state. */
  onRetry?: () => void;
  /** Row's primary action (the entity peek). A bare-row click fires it; a click on an
   *  interactive descendant (a cell `EntityRef` / button) or a MODIFIED click is left to
   *  that element — so the row is a POINTER enhancement of the cell links: no focusable
   *  row, no a11y gap (keyboard + ⌘-click ride the real cell links). */
  onRowActivate?: (row: T) => void;
  /** A list's WALK highlight (FRONTEND-PATTERNS §Row interaction — the opt-in walk for triage / inbox
   *  surfaces whose peek steps with the keys): the row with this id is
   *  marked `aria-current` + highlighted. Purely presentational — the caller
   *  owns the walk (`useRowWalk` — ↑/↓ · j/k); rows stay
   *  non-focusable (the identity cell link remains the keyboard path). Omit
   *  everywhere else — the default table has no highlight. */
  activeRowId?: string | null;
  /** The walk's keys drive: no row paints its hover — the frozen column's included (two highlighted rows mislead);
   *  the page hands hover back on any mouse move (`useRowWalk`'s `keysDriving`). */
  hoverQuiet?: boolean;
  /** Controlled client-side global filter (the input UI is page-owned). */
  globalFilter?: string;
  /** How a row answers the global filter — a page's own matching (the areas tree runs the shared word
   *  rules through `createMatcher`, so the screen and its export agree — ADR-072 §4 · ADR-045). Omit for
   *  TanStack's codepoint substring over the `value` columns. In tree mode a match keeps its ancestors
   *  either way (`filterFromLeafRows`). */
  globalFilterFn?: (row: T, query: string) => boolean;
  /** Controlled sort state (e.g. from the URL-bound list controller, ADR-037). Omit for internal/uncontrolled sort. */
  sorting?: SortingState;
  /** Controlled sort-change handler. Provide alongside `sorting`. */
  onSortingChange?: OnChangeFn<SortingState>;
  /** SERVER-backed sorting: the rows arrive already ordered and the table must
   *  NOT re-sort them client-side (the header toggles only drive the controlled
   *  `sorting` state the page sends to its endpoint). Without this flag a
   *  server-sorted page gets silently RE-sorted by the column `value` fns —
   *  a different criterion than the server's (the double-sort defect). Every
   *  server-backed list controller sets it; client (load-all) lists omit it. */
  manualSorting?: boolean;
  /** Skeleton row count in the loading state. Default 5. */
  skeletonRows?: number;
  /** Freeze the first (identity) column during horizontal scroll (logical inline-start; flips under RTL). The table must sit on a surface background. */
  stickyFirstColumn?: boolean;
  /** Pin the column header while scrolling. With `maxHeight` = the bounded
   *  (app-frame) mode: thead pins inside the scroller. WITHOUT `maxHeight` =
   *  PAGE mode (the doctrine default — one page scroll), TIER-driven: at md+
   *  the container is `overflow-x: clip` and the thead ALWAYS pins against the
   *  viewport at `--dt-sticky-top` (columns must fit — cells truncate; overflow
   *  is a visible column-budget bug + a dev warning, never a silent flip);
   *  below md, horizontal scroll + the frozen identity column win and the
   *  thead rides along (a scroll container can't host viewport-sticky). */
  stickyHeader?: boolean;
  /** Bound the table body to this CSS height and scroll it (a data-grid pane). Enables `stickyHeader`. Omit for the default natural-height table (the page owns vertical scroll). */
  maxHeight?: string;
  /** SCAN-list mobile posture: the fit engine keeps budgeting below md too —
   *  the container clips and the thead (and group bands) pin at EVERY tier;
   *  the yield ladder walks the set down to the phone columns. Omit (default)
   *  for the reference-table posture: below md the table x-scrolls with the
   *  frozen identity column, unpinned — and hides NOTHING (scroll carries
   *  every column; the fit engine only runs while the container clips). */
  mobileFit?: boolean;
  /** Fit-engine report: the ids the current box width dropped (among the
   *  columns passed in). `useColumnVisibility` feeds this to the View gear's
   *  eye-slash annotation. Called only when the set actually changes. */
  onWidthDropChange?: (droppedIds: string[]) => void;
  /** Provide a row's children to make the table an expandable tree-table (TanStack's
   *  expanded row model + getSubRows). The `tree`-marked column gets the indent +
   *  disclosure affordance; the global filter keeps ancestors of a match
   *  (`filterFromLeafRows`). Omit for a flat table — every existing consumer is unchanged. */
  getSubRows?: (row: T) => T[] | undefined;
  /** Controlled expansion state (e.g. force-expand-on-search). Omit for uncontrolled. */
  expanded?: ExpandedState;
  /** Controlled expansion-change handler. Provide alongside `expanded`. */
  onExpandedChange?: OnChangeFn<ExpandedState>;
  /** Initial expansion when uncontrolled (`true` = all rows open, or a `{ [rowId]: true }` map). */
  defaultExpanded?: ExpandedState;
  /** A CLIENT-banded list's two acts on its bands — the gear's "Expand all" · "Collapse all" (`useClientBandActs`):
   *  the page hands a COMMAND — the act, and a count that changes at every press — and the table opens every band,
   *  or returns to its folded arrival (every level above the deepest open, the deepest folded). The table keeps
   *  owning its bands' state (the arrival, the reset on a new criterion); uncontrolled expansion only. A
   *  server-banded list's acts write its open set instead (`lib/bands`). */
  bandsCommand?: BandsCommand;
  /** Group rows into collapsible FULL-WIDTH BANDS by this column's group key
   *  (`groupValue`, falling back to `value`) — the list-system group-by (band
   *  headers, not tree indent). Rides TanStack's grouped row model + the same
   *  expanded model as tree mode (mutually exclusive with `getSubRows`); bands
   *  arrive collapsed (the band line is the overview; expansion is the drill),
   *  and reset to that arrival when the criterion changes. GROUP ORDER
   *  = row order (the caller orders rows by the criterion — a server groups via
   *  ORDER BY); leaf order inside a band follows the active column sort.
   *  An ARRAY nests band levels (e.g. `['category', 'series']`): level-0 bands
   *  pin under the thead; deeper bands ride inline (lighter); every level above
   *  the deepest arrives OPEN (the band tree is the overview, the deepest level
   *  + its rows are the drill). An EMPTY group key renders as a band like any
   *  other — the caller's `groupHeader` gives it its words (e.g. "No series"). */
  groupBy?: string | string[];
  /** Band content for a group — the caller resolves the raw key to a label,
   *  count, and any aggregates. Required when `groupBy` is set. Receives the
   *  grouping COLUMN id third for multi-level tables. */
  groupHeader?: (groupValue: string, leafRows: T[], groupColumnId?: string) => ReactNode;
  /** SERVER-fed bands (ADR-078 §4): the bands from the server, `data` the
   *  stream's page — see `ServerBandsProps`. Mutually exclusive with `groupBy`
   *  (a paginated list never bands in the browser) and `getSubRows`. */
  serverBands?: ServerBandsProps<T>;
  /** First-click sort direction for EVERY column in this table. TanStack
   *  defaults NUMERIC columns to descending-first; a table whose numeric
   *  accessors are a designed low→high scale (rank encodings, band minimums)
   *  passes `false` so the first click always walks the scale upward. */
  sortDescFirst?: boolean;
  className?: string;
}

// A column's `align` applies to BOTH its header and its cells — the SOTA rule:
// the header label sits over its data. `alignClass` aligns the cell + the static
// (non-sortable) header text; `headerJustify` aligns the sortable header's
// flex content (label + sort glyph cluster) to the same side, so a right-aligned
// numeric column's header lines up over its right-aligned numbers (was always
// `justify-between` → label hard-left over right-aligned cells = the misalignment).
const alignClass: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  start: 'text-start',
  end: 'text-end',
  center: 'text-center',
};
const headerJustify: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
};

// Sticky-header stacking: the header row paints above the body cells, and the
// frozen-column corner above both, while scrolling a bounded table. This is a
// LOCAL stacking context inside the table — not one of the app's named global
// layers — so the layering-token rule is deliberately waived here. The PAGE-mode
// pair stays BELOW `--z-nav` (10): a viewport-sticky thead must slide UNDER the
// sticky global nav, never paint over it.
/* eslint-disable local/no-arbitrary-zindex -- intra-table sticky stacking (header > body; frozen corner > both); page-mode pair capped under z-nav */
const Z_STICKY_HEADER = 'z-20';
const Z_STICKY_CORNER = 'z-30';
const Z_PAGE_HEADER = 'z-[5]';
// Sticky group bands: above the z-auto frozen body cells they cover, below the
// page-pinned thead (z-[5]).
const Z_GROUP_BAND = 'z-[4]';
/* eslint-enable local/no-arbitrary-zindex */

// ── the fit engine — the container-true column budget ──────────────────────
// The budget reads the TABLE'S OWN BOX, never the viewport (the space beside a
// rail is the box's, not the window's). Pure arithmetic over DECLARED floors —
// never measured content (content-driven behavior flips are the doctrine's
// forbidden surprise); deterministic, no reflow loop, columns return the
// moment there's room.

/** Fallback floor when a column declares no `minWidth`. Conservative — narrow
 *  columns (badges, short numbers) should declare a tighter honest floor. */
export const COLUMN_FLOOR_DEFAULT = '6rem';

/** Per-kind floor suggestions (border-box, cell padding included) — reference
 *  these for consistency; override per column where the content says so. */
export const COLUMN_FLOORS = {
  text: '8rem',
  code: '7rem',
  date: '7rem',
  number: '5rem',
  badge: '6rem',
  actions: '3.5rem',
} as const;

// Unranked drops yield after every explicit rank (< 10 000); unranked degrades
// after all drops. Later-in-DOM yields first within each implicit band.
const IMPLICIT_DROP_BASE = 10_000;
const IMPLICIT_DEGRADE_BASE = 100_000;

const parseFloorPx = (v: string | undefined, rootFontPx: number, fallbackPx: number): number => {
  if (!v) return fallbackPx;
  const n = parseFloat(v);
  if (Number.isNaN(n)) return fallbackPx;
  return v.trimEnd().endsWith('rem') ? n * rootFontPx : n;
};

export interface ColumnFit {
  dropped: ReadonlySet<string>;
  degraded: ReadonlySet<string>;
}

const EMPTY_FIT: ColumnFit = { dropped: new Set(), degraded: new Set() };

/** The one fit pass: walk the yield ladder (drops, then default-last degrades,
 *  ascending rank, one step at a time) until Σ floors ≤ the container. Pure —
 *  unit-tested directly; `containerPx <= 0` means "unmeasured" (hidden tab,
 *  jsdom) and drops nothing.
 *
 *  `extraSteps` is the TRUTH-CORRECTION dial (the T structural fix): floors
 *  are author-declared constants calibrated in ONE language, and a wider
 *  locale (FR headers, longer badges) can exceed them — the floor maths then
 *  says "fits" while the clipping container silently cuts the tail columns,
 *  and the gear still calls them visible (the architect's repeated FR bug).
 *  The component measures REAL overflow after paint and re-runs the fit with
 *  extraSteps+1 until the table truly fits — the ladder itself stays the one
 *  yield order, so correction is just "walk further down the same ladder". */
export function computeColumnFit<T>(
  cols: readonly DataTableColumn<T>[],
  containerPx: number,
  rootFontPx: number,
  extraSteps = 0,
): ColumnFit {
  if (containerPx <= 0 || cols.length === 0) return EMPTY_FIT;
  const fallback = parseFloorPx(COLUMN_FLOOR_DEFAULT, rootFontPx, 96);
  const floors = new Map(cols.map((c) => [c.id, parseFloorPx(c.floor ?? c.minWidth, rootFontPx, fallback)]));
  let sum = 0;
  for (const px of floors.values()) sum += px;
  if (sum <= containerPx && extraSteps <= 0) return EMPTY_FIT;

  type Step = { kind: 'drop' | 'degrade'; id: string; rank: number; index: number; saving: number };
  const steps: Step[] = [];
  cols.forEach((c, i) => {
    const never = i === 0 || c.neverDrop; // index 0 = the identity anchor
    if (!never) {
      steps.push({
        kind: 'drop',
        id: c.id,
        rank: c.dropOrder ?? IMPLICIT_DROP_BASE + (cols.length - i),
        index: i,
        saving: floors.get(c.id)!,
      });
    } else if (c.degraded) {
      steps.push({
        kind: 'degrade',
        id: c.id,
        rank: c.degradeOrder ?? IMPLICIT_DEGRADE_BASE + (cols.length - i),
        index: i,
        saving: Math.max(0, floors.get(c.id)! - parseFloorPx(c.degraded.minWidth, rootFontPx, fallback)),
      });
    }
  });
  // Ascending rank; equal ranks yield LATER-in-DOM first (importance reads
  // left→right, so the rightmost of a band is the first to go).
  steps.sort((a, b) => a.rank - b.rank || b.index - a.index);

  const dropped = new Set<string>();
  const degraded = new Set<string>();
  let extra = extraSteps;
  for (const step of steps) {
    if (sum <= containerPx) {
      if (extra <= 0) break;
      extra -= 1; // the correction: keep yielding past the floor maths
    }
    (step.kind === 'drop' ? dropped : degraded).add(step.id);
    sum -= step.saving;
  }
  return { dropped, degraded };
}

function SortGlyph({ dir }: { dir: false | 'asc' | 'desc' }) {
  if (dir === 'asc') return <icons.caretUp aria-hidden size={14} />;
  if (dir === 'desc') return <icons.caretDown aria-hidden size={14} />;
  return <icons.sort aria-hidden size={14} className="opacity-40" />;
}

// Show the refreshing pulse only for refetches that actually take a moment. A
// sub-threshold refetch (a warm-cache re-fetch on back-navigation) would otherwise
// flash the strip for a few imperceptible ms — the flash-of-loading footgun. Delay
// the flag so quick refetches never paint it; slow ones still signal.
function useDelayedTrue(active: boolean, ms: number): boolean {
  const [delayed, setDelayed] = useState(false);
  // Reset synchronously during render when inactive (the codebase's resync-in-
  // render pattern, like useDebouncedSearch — never a setState-in-effect). The
  // timer below only ever sets it true (async, in a callback — lint-clean).
  if (!active && delayed) setDelayed(false);
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => setDelayed(true), ms);
    return () => clearTimeout(timer);
  }, [active, ms]);
  return delayed && active;
}

export function DataTable<T extends RowData>({
  columns,
  data,
  getRowId,
  caption,
  isPending = false,
  isFetching = false,
  isError = false,
  empty,
  errorMessage,
  onRetry,
  onRowActivate,
  activeRowId,
  hoverQuiet = false,
  globalFilter,
  globalFilterFn,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  manualSorting = false,
  skeletonRows = 5,
  stickyFirstColumn = false,
  stickyHeader = false,
  maxHeight,
  mobileFit = false,
  onWidthDropChange,
  getSubRows,
  expanded: controlledExpanded,
  onExpandedChange,
  defaultExpanded,
  bandsCommand,
  groupBy,
  groupHeader,
  serverBands,
  sortDescFirst,
  className = '',
}: DataTableProps<T>) {
  const { t, i18n } = useTranslation('common');
  // The refreshing pulse waits ~400ms so a fast (warm-cache) refetch doesn't flash.
  const showRefreshing = useDelayedTrue(isFetching && data != null, 400);

  // Locale-aware sort. TanStack's built-in `text` fn compares by code
  // point, so Arabic letters and accented French (é after z) sort wrong. An
  // `Intl.Collator` keyed off the active locale fixes the alphabetical order;
  // `numeric: true` also gives natural number-in-string order. Numbers/Dates keep
  // their type-correct compare (the collator is for text). Re-created on a locale
  // switch so the order tracks the UI language.
  const collator = useMemo(() => new Intl.Collator(i18n.language, { numeric: true }), [i18n.language]);
  const localeSort = useMemo<SortFn<Features, T>>(
    () => (rowA, rowB, columnId) => {
      const a = rowA.getValue(columnId);
      const b = rowB.getValue(columnId);
      if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
      if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : a > b ? 1 : 0;
      return collator.compare(String(a ?? ''), String(b ?? ''));
    },
    [collator],
  );

  // Scroll-aware shadow: flag which axes have scrolled so the sticky header /
  // frozen column cast a soft shadow only over content actually beneath them.
  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.dataset.scrolledY = el.scrollTop > 0 ? 'on' : 'off';
    el.dataset.scrolledX = Math.abs(el.scrollLeft) > 0 ? 'on' : 'off';
  }, []);

  // PAGE-mode sticky thead (stickyHeader without maxHeight — the one-page-scroll
  // doctrine). The pin is TIER-driven, never content-driven (a content-measured
  // flip silently lost the pin on a laptop whose table ran a few px wide — the
  // exact surprise the doctrine forbids):
  //   • md+ (post-hamburger web layouts): the container is `overflow-x: clip`
  //     (viewport-sticky needs a NON-scroll container) and the thead ALWAYS
  //     pins. Columns MUST fit — cells truncate (min-w-0 + truncate in the
  //     cell markup); genuine overflow is a column-budget bug made VISIBLE
  //     (clipped edge + the dev warning below), never a silent behavior flip.
  //   • below md: horizontal scroll + the frozen identity column win by CSS
  //     necessity (a scroll container can't host viewport-sticky) — unpinned.
  //   • `mobileFit` (a SCAN list that declared a mobile column budget): the
  //     necessity is gone — clip + pin at every tier.
  const pageSticky = stickyHeader && !maxHeight;
  // The scroll/clip container held as STATE (callback ref): the element swaps
  // across the loading→populated branches, and every observer keyed on it
  // re-attaches — a plain ref read once at mount would watch the dead branch.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [mdUp, setMdUp] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 768px)').matches
      : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setMdUp(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const pinned = pageSticky && (mdUp || mobileFit);

  // (The fit measurement lives below, after `renderColumns` — it re-renders
  // ONLY when the computed set changes, never per resize frame. The overflow
  // TRUTH-CORRECTION pass lives beside it: declared floors are calibrated in
  // one language, so a wider locale can beat them — the engine then measures
  // real post-paint overflow and walks the ladder further instead of letting
  // the clip container silently cut columns the gear still calls visible.)

  // Pinned-state cue: the page-mode thead casts the soft scroll-under shadow
  // only once it is actually STUCK (CSS has no :stuck — a sentinel above the
  // table + an IntersectionObserver at the sticky offset detects it; a data
  // attribute flips, no re-render). At rest the thead sits flush — no shadow.
  const pinSentinelRef = useRef<HTMLDivElement>(null);
  // The offset is read from the CSS that actually positions the thead; the
  // number from lib/shellTop is the RE-ARM signal — a project strip arriving, or
  // a condensed bar taking the top, moves the pin line under a mounted table.
  const stickyTopPx = useStickyTopPx();
  useEffect(() => {
    if (!pageSticky) return;
    const sentinel = pinSentinelRef.current;
    const container = scrollEl;
    if (!sentinel || !container || typeof IntersectionObserver === 'undefined') return;
    const raw = getComputedStyle(container).getPropertyValue('--dt-sticky-top').trim();
    const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const offset = raw.endsWith('rem') ? parseFloat(raw) * rootFont : parseFloat(raw) || 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        container.dataset.pinned = entry && !entry.isIntersecting ? 'on' : 'off';
      },
      { rootMargin: `-${offset + 1}px 0px 0px 0px`, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pageSticky, scrollEl, stickyTopPx]);

  // Sticky group bands need the thead's real height as their top offset —
  // measured once (+ on resize) into a container CSS var, so the band's
  // `top: calc(--dt-sticky-top + --dt-thead-h)` stays true across densities.
  const theadRef = useRef<HTMLTableSectionElement>(null);
  useEffect(() => {
    const container = scrollEl;
    const thead = theadRef.current;
    if (!container || !thead || typeof ResizeObserver === 'undefined') return;
    const measure = () => container.style.setProperty('--dt-thead-h', `${thead.getBoundingClientRect().height}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [scrollEl]);

  // Sort is controlled when the caller passes `sorting` (the URL-bound list
  // controller, ADR-037); otherwise DataTable owns it internally (static use).
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? internalSorting;
  const handleSortingChange = controlledOnSortingChange ?? setInternalSorting;

  // Expansion is controlled when the caller passes `expanded` (e.g. the administrativeAreas
  // tree force-expands matches under search); otherwise DataTable owns it. Tree
  // wins if both `getSubRows` and `groupBy` are (mis)passed.
  const isTree = !!getSubRows;
  // groupBy normalizes to LEVELS (single string = one level); the joined key is
  // the criterion identity for reset comparisons (stable across re-renders even
  // when the caller passes an inline array literal).
  const groupCols = useMemo(() => (groupBy && !serverBands ? (Array.isArray(groupBy) ? groupBy : [groupBy]) : []), [groupBy, serverBands]);
  const groupKey = groupCols.join('\u0000');
  const isGrouped = groupCols.length > 0 && !isTree;

  // Groups ARRIVE COLLAPSED — the band line (label · count · aggregates) is the
  // overview; expansion is the drill. The expansion map holds per-band ids
  // (`${columnId}:${value}`); `expanded: true` (which opens every band) is set
  // by ONE deliberate act alone — the gear's "Expand all" (`bandsCommand`).
  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>(defaultExpanded ?? {});
  const expanded = controlledExpanded ?? internalExpanded;
  const handleExpandedChange = onExpandedChange ?? setInternalExpanded;

  // Switching the group criterion resets to the collapsed arrival (stale
  // per-id expansion keys must not leak across criteria) — render-phase
  // resync, never an effect.
  const [prevGroupKey, setPrevGroupKey] = useState(groupKey);
  if (prevGroupKey !== groupKey) {
    setPrevGroupKey(groupKey);
    if (isGrouped) setInternalExpanded(defaultExpanded ?? {});
  }

  const tableColumns = useMemo<ColumnDef<Features, T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        // Blanks (null / '') become undefined so `sortUndefined: 'last'` keeps
        // them at the bottom in BOTH directions — the "— pile on top" fix.
        accessorFn: col.value
          ? (row: T) => {
              const v = col.value!(row);
              return v === null || v === '' ? undefined : v;
            }
          : () => undefined,
        enableSorting: !!col.sortable,
        enableGlobalFilter: !!col.value,
        sortFn: localeSort,
        sortUndefined: 'last' as const,
        ...(col.sortDescFirst !== undefined ? { sortDescFirst: col.sortDescFirst } : {}),
        ...(col.groupValue ? { getGroupingValue: col.groupValue } : {}),
      })),
    [columns, localeSort],
  );

  // The columns that actually RENDER: grouping-only columns carry a band key
  // (in tableColumns above, so `groupBy` + getGroupingValue still resolve) but
  // paint nothing — excluded here from every header/cell/skeleton map + the band colspan.
  const renderColumns = useMemo(() => columns.filter((c) => !c.groupingOnly), [columns]);

  // ── the fit engine's ONE measurement ────────────────────────────────────
  // The clip container's own width — never the viewport. Fit runs ONLY while
  // the container clips (md+ page pin, or mobileFit at every tier); scroll
  // postures — below-md reference tables, bounded analytical grids — hide
  // nothing: scroll carries every column. Width 0 = hidden/unmeasured →
  // computeColumnFit drops nothing until a real width arrives.
  //
  // CRITICAL: the raw width never enters React state. The observer recomputes
  // the fit on every frame of a resize (cheap arithmetic) but commits state
  // ONLY when the resulting set actually changes — between thresholds a drag
  // re-renders nothing (CSS alone stretches the columns, smooth); crossing a
  // threshold re-renders exactly once. Storing the width itself re-rendered
  // the whole table per frame — the "everything dances while resizing" bug.
  // Layout effect so the first fit lands before paint (no flash of a wrong set).
  const [fit, setFit] = useState<ColumnFit>(EMPTY_FIT);
  // The truth-correction dial: extra ladder steps applied on top of the floor
  // maths, driven by MEASURED post-paint overflow (see the corrector effect
  // below). Reset whenever the floor-based baseline changes (a resize crossing
  // a threshold, a column-set change) so columns can come back on widen and
  // the correction re-converges from the honest base.
  const [extraSteps, setExtraSteps] = useState(0);
  // A correction is valid for EXACTLY ONE configuration: this column set, at
  // widths at-or-below the one it was learned at (overflow is monotone under
  // narrowing). So it invalidates on (a) ANY column-set change — the gear! —
  // and (b) a real widen past the learning width (+24px hysteresis against
  // drag jitter), then re-converges from the measured truth. Keying the reset
  // on the base-fit RESULT was the T gear bug: at comfortable widths the base
  // is empty for EVERY set, so a gear-hide kept the stale steps armed and
  // they executed against the NEW set — killing the next ladder column
  // instead of freeing space.
  const columnsKeyRef = useRef('');
  const correctionWidthRef = useRef(0);
  // True while a fit commit is on its way to the DOM — the corrector below
  // must measure the SETTLED paint, never the pre-update one (bumping off a
  // stale measurement over-drops by one).
  const fitSettlingRef = useRef(false);
  useLayoutEffect(() => {
    // Unpinned, nothing is measured — `activeFit` below reads EMPTY_FIT then.
    if (!scrollEl || !pinned) return;
    const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>) => a.size === b.size && [...a].every((id) => b.has(id));
    const compute = () => {
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const width = scrollEl.clientWidth;
      const columnsKey = renderColumns.map((c) => c.id).join(' ');
      let extras = extraSteps;
      if (columnsKey !== columnsKeyRef.current || width > correctionWidthRef.current + 24) {
        columnsKeyRef.current = columnsKey;
        correctionWidthRef.current = width;
        extras = 0;
        setExtraSteps((prev) => (prev === 0 ? prev : 0));
      }
      const next = computeColumnFit(renderColumns, width, rootFont, extras);
      setFit((prev) => {
        if (sameSet(prev.dropped, next.dropped) && sameSet(prev.degraded, next.degraded)) return prev;
        fitSettlingRef.current = true;
        return next;
      });
    };
    compute();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(compute);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [scrollEl, pinned, renderColumns, extraSteps]);
  // The fit the render reads: the measured one while the container clips,
  // nothing dropped otherwise — derived here, never reset from an effect.
  const activeFit = scrollEl && pinned ? fit : EMPTY_FIT;

  // ── the overflow TRUTH-CORRECTION (the T structural fix) ────────────────
  // After every settled paint under a pin, measure the scroller. Real overflow
  // means the declared floors lied for the CURRENT content/locale (FR headers
  // run wider than the EN-calibrated floors) — walk one more ladder step and
  // let the fit effect recompute; repeat until it truly fits. Converges in
  // ≤ ladder-length renders (in practice 1–2). When the ladder is spent
  // (never-drop columns alone still overflow), warn in dev — that residue is
  // a genuine authoring bug (raise the compact faces' floors / truncate).
  const [, forceMeasure] = useReducer((n: number) => n + 1, 0);
  useLayoutEffect(() => {
    if (fitSettlingRef.current) {
      // A fit change was committed this pass — measure the SETTLED paint on a
      // forced follow-up (a resize-driven commit may be the last render, so
      // waiting passively for "the next one" could stall the correction).
      fitSettlingRef.current = false;
      const id = requestAnimationFrame(() => forceMeasure());
      return () => cancelAnimationFrame(id);
    }
    if (!scrollEl || !pinned) return;
    if (scrollEl.scrollWidth <= scrollEl.clientWidth + 1) return;
    const stepsAvailable = renderColumns.filter((c, i) => !(i === 0 || c.neverDrop) || c.degraded).length;
    if (extraSteps < stepsAvailable) {
      // Record the width this step was LEARNED at — a widen past it (+24px)
      // is what invalidates the correction, so it must track the bump, not
      // only the reset (a bump after an un-reset narrow would otherwise pin
      // the stale learning width and strand the over-drop on re-widen).
      correctionWidthRef.current = scrollEl.clientWidth;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- a layout measurement corrected before paint (the documented use of a layout effect); deferring it would paint the clipped columns first
      setExtraSteps(extraSteps + 1);
      return;
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only author guardrail (guarded by import.meta.env.DEV)
      console.warn(
        `DataTable: columns overflow their container by ${scrollEl.scrollWidth - scrollEl.clientWidth}px with the yield ladder EXHAUSTED — the never-drop set's real content exceeds its compact floors. Raise the degraded minWidths or add cell truncation.`,
      );
    }
  });

  // The ONE visible set every render path reads (thead, cells, skeleton, band
  // colspans) — degraded columns swap to their compact face, id unchanged so
  // sorting/grouping never notice.
  const fitColumns = useMemo(
    () =>
      renderColumns
        .filter((c) => !activeFit.dropped.has(c.id))
        .map((c) =>
          activeFit.degraded.has(c.id) && c.degraded
            ? { ...c, header: c.degraded.header ?? c.header, cell: c.degraded.cell, minWidth: c.degraded.minWidth, width: undefined }
            : c,
        ),
    [renderColumns, activeFit],
  );

  // Report the width-dropped ids upward (the gear's eye-slash) on change only.
  const lastDropReportRef = useRef('');
  useEffect(() => {
    if (!onWidthDropChange) return;
    const ids = renderColumns.filter((c) => activeFit.dropped.has(c.id)).map((c) => c.id);
    const key = ids.join('\u0000');
    if (key === lastDropReportRef.current) return;
    lastDropReportRef.current = key;
    onWidthDropChange(ids);
  }, [activeFit, renderColumns, onWidthDropChange]);

  // STABLE grouping state — a per-render `[groupBy]` literal would make the
  // grouped row model recompute on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- groupKey IS groupCols' identity
  const groupingState = useMemo(() => (isGrouped ? [...groupCols] : []), [isGrouped, groupKey]);

  // Every option and state slice is passed on EVERY render, in every mode: the
  // engine merges each render's options over the last ones and keeps the last
  // controlled state as its own, so a slice or a handler left out when a mode
  // ends would live on (grouping switched off would keep the old bands).
  const table = useTable({
    features,
    data: data ?? [],
    columns: tableColumns,
    // Table-level first-click direction (undefined keeps TanStack's inference:
    // numeric accessors sort descending-first).
    sortDescFirst,
    state: {
      sorting,
      globalFilter: globalFilter ?? '',
      expanded,
      grouping: groupingState,
    },
    onSortingChange: handleSortingChange,
    // Server-backed pages: rows render exactly as received — the header toggle
    // only drives the controlled state the page's endpoint consumes. A server-fed
    // stream is ALWAYS such a page: a client sort would split its runs.
    manualSorting: manualSorting || !!serverBands,
    getRowId: (row) => getRowId(row),
    // Expansion is OURS in every mode (the band resync on a new criterion, a
    // tree page's own opening and search) — the engine never resets it. It
    // would on every change of `data` (verified in table-core), so a page that
    // rebuilds its rows each render would never stay open.
    autoResetExpanded: false,
    onExpandedChange: handleExpandedChange,
    getSubRows: isTree ? getSubRows : undefined,
    // A leaf match keeps its ancestor chain visible (ancestor-preserving search).
    filterFromLeafRows: isTree,
    // A page's own matching, when it has one; else TanStack's substring over the `value` columns.
    ...(globalFilterFn ? { globalFilterFn: (row: { original: T }, _columnId: string, value: unknown) => globalFilterFn(row.original, String(value ?? '')) } : {}),
    // Keep a grouped column exactly where it is — bands carry the group
    // identity; the column set never reshapes under grouping.
    groupedColumnMode: false,
    // 2-state sort: a header click cycles asc⇄desc and never "unsorts". Removal is
    // OFF deliberately — with an always-present default sort, the 3rd-click "clear"
    // only reverted to that default, and on a NUMERIC column whose default is asc it
    // got STUCK: numeric columns are sortDescFirst, so asc is the removal-next state →
    // the click removed the sort → useUrlState's codec reverted null to the same asc
    // default → the column could never reach desc (hit the responsibilities Level
    // column). A 2-state toggle is bug-proof for any column type / default direction.
    enableSortingRemoval: false,
  });

  // Multi-level arrival: the band TREE is the overview — every level above the
  // deepest arrives OPEN, the deepest level stays collapsed (its rows are the
  // drill). Single-level tables build an empty map — the register's collapsed
  // arrival, unchanged. Per-band ids from the REAL grouped model, never
  // `expanded: true`. Keyed on criterion + first-data so later filter changes
  // keep the user's open/closed state.
  const arrival = (): Record<string, boolean> => {
    const open: Record<string, boolean> = {};
    if (groupCols.length > 1) {
      for (const r of table.getGroupedRowModel().flatRows) {
        if (r.getIsGrouped() && r.depth < groupCols.length - 1) open[r.id] = true;
      }
    }
    return open;
  };
  const [prevArrivalKey, setPrevArrivalKey] = useState('');
  const arrivalKey = isGrouped && groupCols.length > 1 && data ? groupKey : '';
  if (prevArrivalKey !== arrivalKey) {
    setPrevArrivalKey(arrivalKey);
    if (arrivalKey !== '' && controlledExpanded === undefined) setInternalExpanded(arrival());
  }

  // The gear's two acts on a client-banded list: every band open, or back to the folded arrival — a command from the
  // page, applied once per press (render-phase, like the resyncs above; never an effect).
  const [prevCommand, setPrevCommand] = useState(bandsCommand?.n);
  if (bandsCommand && bandsCommand.n !== prevCommand) {
    setPrevCommand(bandsCommand.n);
    if (isGrouped && controlledExpanded === undefined) setInternalExpanded(bandsCommand.act === 'open' ? true : arrival());
  }

  // ── error ──────────────────────────────────────────────────────────────
  if (isError && !data) {
    return (
      <div className={className}>
        <Alert variant="danger">{errorMessage ?? t('table.errorTitle')}</Alert>
        {onRetry && (
          <div className="mt-3">
            <Button variant="secondary" onClick={onRetry}>
              {t('actions.retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── loading ────────────────────────────────────────────────────────────
  if (isPending && !data) {
    return (
      <div
        ref={setScrollEl}
        role="status"
        aria-live="polite"
        className={`overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] ${className}`}
      >
        <span className="sr-only">{t('statuses.loading')}</span>
        <table className="w-full border-collapse">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="bg-[var(--color-bg-surface-alt)]">
              {fitColumns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  style={{ width: col.width, minWidth: col.minWidth }}
                  className={`px-4 py-2 text-sm font-semibold text-[var(--color-text-secondary)] ${alignClass[col.align ?? 'start']}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: skeletonRows }).map((_, r) => (
              <tr key={r} className="border-t border-[var(--color-border)]">
                {fitColumns.map((col) => (
                  <td key={col.id} className="px-4 py-[var(--density-cell-py)]">
                    <Skeleton />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── empty ──────────────────────────────────────────────────────────────
  // Keyed off the FILTERED row model, not raw data — a globalFilter matching
  // nothing must show the empty state, never a silently blank tbody.
  const rows = table.getRowModel().rows;
  if (data && rows.length === 0 && !serverBands?.bands.length) {
    return (
      <div className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] ${className}`}>
        {empty ?? <EmptyState title={t('table.emptyDefault')} />}
      </div>
    );
  }

  // Which pin applies: 'bounded' (inside the maxHeight scroller) or 'page'
  // (against the viewport, under the nav) — never both. Group bands stick
  // directly under whichever thead pin is active.
  const headerPin = stickyHeader && maxHeight ? 'bounded' : pinned ? 'page' : null;

  // The server-fed stream: the page's layout from the rows' stamps and the bands' positions.
  const isOpenBand = (key: string) => serverBands !== undefined && (serverBands.open === '*' || serverBands.open.has(key));
  const segments = serverBands ? streamSegments(serverBands.bands, isOpenBand, rows.map((r) => serverBands.bandOf(r.original)), serverBands.page) : null;

  // ── the band row — ONE markup for both band sources ───────────────────
  // A full-width row: a real toggle button (the band content is its accessible
  // name) that stays pinned to the inline start while the columns scroll
  // horizontally, so the band never blanks out on a narrow viewport; the whole
  // band is a pointer convenience for the same toggle. The band cell PINS under
  // the active thead pin (page: below --dt-sticky-top + the measured thead;
  // bounded: below the in-scroller thead) so you always know which group you
  // are in; the next band's row replaces it on arrival — native sticky overlap,
  // later-DOM paints on top. z-[4]: above the z-auto frozen body cells it
  // covers, below the thead (5). CONTENT, not chrome: the band is a data
  // summary, so it sits on the content surface with typography doing the work
  // — the chrome gray (surface-alt) stays reserved for the thead strip. The
  // inset bottom hairline replaces a border (collapse-borders don't travel with
  // sticky cells) so rows scrolling under a stuck band shear against a line.
  const bandRow = (o: { id: string; expanded: boolean; onToggle: () => void; nested: boolean; afterBand: boolean; content: ReactNode; aside?: ReactNode }) => (
    <tr
      key={o.id}
      onClick={(e) => {
        // A click inside an overlay a cell opened (a portal: React's tree is the row's, the DOM is not) is not a click on the band.
        if (!e.currentTarget.contains(e.target as Node)) return;
        if (e.defaultPrevented) return;
        if ((e.target as HTMLElement).closest('a,button,input,select,textarea,label')) return;
        o.onToggle();
      }}
      className={`cursor-pointer ${o.afterBand ? '' : 'border-t border-[var(--color-border)] '}${o.nested ? 'bg-[var(--color-bg-surface)]' : 'bg-[var(--color-bg-surface-soft)]'}`}
    >
      <td
        colSpan={fitColumns.length}
        className={`p-0 shadow-[inset_0_-1px_0_var(--color-border)] ${!o.nested && headerPin ? `sticky ${Z_GROUP_BAND} bg-[var(--color-bg-surface-soft)]` : ''}`}
        style={
          // -1px: tucked under the thead (which paints above) so a fractional-height rounding seam can't let content
          // peek through between the two stuck strips.
          !o.nested && headerPin === 'page'
            ? { top: 'calc(var(--dt-sticky-top, 0px) + var(--dt-thead-h, 2.5rem) - 1px)' }
            : !o.nested && headerPin === 'bounded'
              ? { top: 'calc(var(--dt-thead-h, 2.5rem) - 1px)' }
              : undefined
        }
      >
        {/* The whole line pins at the start edge — the label and what rides beside it move together on a table
            that scrolls sideways (a sticky button alone would let the aside slide under it). */}
        <div className="sticky start-0 flex max-w-max items-center">
          <button
            type="button"
            onClick={o.onToggle}
            aria-expanded={o.expanded}
            className={`flex items-center gap-2 px-4 outline-none focus-ring-inset ${
              // Nested bands hold the SAME start edge (the register's flat-band discipline — no indentation
              // staircase); weight + tint carry the hierarchy.
              o.nested ? 'py-2 text-sm font-medium text-[var(--color-text-primary)]' : 'py-2.5 text-sm font-semibold text-[var(--color-text-primary)]'
            }`}
          >
            {o.expanded ? (
              <icons.caretDown aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
            ) : (
              <icons.caretRight aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)] rtl:rotate-180" />
            )}
            {o.content}
          </button>
          {o.aside}
        </div>
      </td>
    </tr>
  );

  // ── the leaf row — the same in every mode ──────────────────────────────
  const leafRow = (row: (typeof rows)[number], afterBand: boolean) => {
    const active = activeRowId != null && row.id === activeRowId;
    return (
    <tr
      key={row.id}
      onClick={
        onRowActivate
          ? (e) => {
              // Pointer enhancement: a bare-row click peeks the row's entity. Interactive descendants (cell
              // EntityRefs / buttons) and modified clicks (⌘/ctrl/shift/alt → new-tab on the real links) are left
              // to do their own thing — so no per-cell stopPropagation is needed. A click inside an overlay a cell
              // opened (an `InfoPopover`'s panel — a portal: React's tree is the row's, the DOM is not) is not a
              // row click.
              if (!e.currentTarget.contains(e.target as Node)) return;
              if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              if ((e.target as HTMLElement).closest('a,button,input,select,textarea,label,[role="button"],[role="menuitem"]')) return;
              onRowActivate(row.original);
            }
          : undefined
      }
      aria-current={active ? 'true' : undefined}
      className={`group ${afterBand ? '' : 'border-t border-[var(--color-border)] '}${
        // The walk's highlighted row: the selection tint, hover kept (the tint is a state, not a hover suppressor) —
        // unless the walk's keys drive, when no row paints its hover.
        active ? 'bg-[var(--color-primary-light)] ' : ''
      }${hoverQuiet ? '' : 'hover:bg-[var(--color-bg-hover)]'}${onRowActivate ? ' cursor-pointer' : ''}`}
    >
      {fitColumns.map((col, i) => (
        <td
          key={col.id}
          className={`px-4 py-[var(--density-cell-py)] text-sm text-[var(--color-text-primary)] ${alignClass[col.align ?? 'start']} ${
            stickyFirstColumn && i === 0
              ? // Opaque surface base + the row's tints LAYERED on top (a gradient background-image, not a bg-color
                // swap) so the frozen column stays fully opaque — otherwise the translucent tints let the scrolling
                // cells show through during horizontal scroll — and paints them as the row does: the walk's
                // highlight, and the hover unless the keys drive.
                `sticky start-0 bg-[var(--color-bg-surface)] ${active ? 'bg-[linear-gradient(var(--color-primary-light),var(--color-primary-light))] ' : ''}${hoverQuiet ? '' : 'group-hover:bg-[linear-gradient(var(--color-bg-hover),var(--color-bg-hover))] '}ltr:group-data-[scrolled-x=on]/dt:scroll-shadow-e rtl:group-data-[scrolled-x=on]/dt:scroll-shadow-e-rtl`
              : ''
          }`}
        >
          {col.tree && isTree ? (
            <span className="flex items-center gap-1.5" style={{ paddingInlineStart: `${row.depth * 1.25}rem` }}>
              {row.getCanExpand() ? (
                <button
                  type="button"
                  onClick={row.getToggleExpandedHandler()}
                  aria-expanded={row.getIsExpanded()}
                  aria-label={row.getIsExpanded() ? t('actions.collapse') : t('actions.expand')}
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded text-[var(--color-text-secondary)] outline-none hover:bg-[var(--color-bg-hover)] focus-ring cursor-pointer"
                >
                  {row.getIsExpanded() ? <icons.caretDown aria-hidden size={14} /> : <icons.caretRight aria-hidden size={14} className="rtl:rotate-180" />}
                </button>
              ) : (
                <span aria-hidden className="inline-block size-5 shrink-0" />
              )}
              {col.cell(row.original)}
            </span>
          ) : (
            col.cell(row.original)
          )}
        </td>
      ))}
    </tr>
    );
  };

  // The server band's header line: the surface's label · "continued" when the page starts inside the band · the count
  // (a floor prints "n+"); beside the button, where you are in a band that spans pages (not on the phone). The
  // separator is drawn, not spoken (it is outside the words, so it never enters the button's name); the count is
  // pre-formatted (the Numerals axis), passed as `n` — never `count`, which i18next would read as a plural.
  const serverBandRow = (seg: Extract<StreamSegment, { kind: 'band' }>, afterBand: boolean) =>
    bandRow({
      id: seg.id,
      expanded: seg.open,
      onToggle: () => serverBands?.onToggle(seg.band.key, !seg.open),
      nested: false,
      afterBand,
      content: (
        <>
          {serverBands?.renderLabel(seg.band)}
          {seg.continued && (
            <span className="font-normal text-[var(--color-text-secondary)]">
              <span aria-hidden>· </span>
              {t('table.bandContinued')}
            </span>
          )}
          <span className="rounded-full bg-[var(--color-bg-surface)] px-1.5 py-px text-xs font-medium tabular-nums text-[var(--color-text-secondary)]">
            {seg.band.capped ? t('table.countFloor', { n: formatNumber(seg.band.count) }) : formatNumber(seg.band.count)}
          </span>
        </>
      ),
      aside: seg.position ? (
        <span className="hidden text-xs tabular-nums text-[var(--color-text-secondary)] sm:inline">
          {t(seg.band.capped ? 'table.bandRowsCapped' : 'table.bandRows', { from: formatNumber(seg.position.from), to: formatNumber(seg.position.to), total: formatNumber(seg.band.count) })}
        </span>
      ) : undefined,
    });

  // ── populated (+ refreshing) ─────────────────────────────────────────────
  return (
    <div className={className}>
      {showRefreshing && (
        <div role="status" aria-live="polite">
          <div aria-hidden className="h-0.5 animate-pulse rounded-full bg-[var(--color-primary-display)]" />
          <span className="sr-only">{t('statuses.loading')}</span>
        </div>
      )}
      <div
        ref={setScrollEl}
        className={`group/dt ${
          maxHeight ? 'overflow-auto' : pinned ? 'overflow-x-clip' : 'overflow-x-auto'
        } rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]`}
        style={maxHeight ? { maxHeight } : undefined}
        onScroll={handleScroll}
      >
        {pageSticky && <div ref={pinSentinelRef} aria-hidden className="h-0" />}
        <table className="w-full border-collapse">
          <caption className="sr-only">{caption}</caption>
          <thead ref={theadRef}>
            <tr className="bg-[var(--color-bg-surface-alt)]">
              {fitColumns.map((col, i) => {
                const tcol = table.getColumn(col.id);
                const sorted = (tcol?.getIsSorted() ?? false) as false | 'asc' | 'desc';
                const isFirst = stickyFirstColumn && i === 0;
                const headSticky = [
                  headerPin === 'bounded' ? 'sticky top-0 bg-[var(--color-bg-surface-alt)] group-data-[scrolled-y=on]/dt:scroll-shadow-b' : '',
                  headerPin === 'page' ? `sticky bg-[var(--color-bg-surface-alt)] ${Z_PAGE_HEADER} group-data-[pinned=on]/dt:scroll-shadow-b` : '',
                  isFirst ? 'sticky start-0 bg-[var(--color-bg-surface-alt)] ltr:group-data-[scrolled-x=on]/dt:scroll-shadow-e rtl:group-data-[scrolled-x=on]/dt:scroll-shadow-e-rtl' : '',
                  headerPin === 'bounded' ? (isFirst ? Z_STICKY_CORNER : Z_STICKY_HEADER) : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <th
                    key={col.id}
                    scope="col"
                    aria-sort={
                      sorted === 'asc'
                        ? 'ascending'
                        : sorted === 'desc'
                          ? 'descending'
                          : col.sortable
                            ? 'none'
                            : undefined
                    }
                    style={{ width: col.width, minWidth: col.minWidth, ...(headerPin === 'page' ? { top: 'var(--dt-sticky-top, 0px)' } : {}) }}
                    className={`text-sm font-semibold text-[var(--color-text-secondary)] ${alignClass[col.align ?? 'start']} ${headSticky}`}
                  >
                    {col.sortable ? (
                      <Button
                        variant="ghost"
                        className={`w-full ${headerJustify[col.align ?? 'start']} gap-1 font-semibold`}
                        onClick={tcol?.getToggleSortingHandler()}
                      >
                        <span>{col.header}</span>
                        <SortGlyph dir={sorted} />
                      </Button>
                    ) : (
                      // Same 40px box as the sortable header's Button (min-h-[2.5rem]),
                      // so a table with no sortable columns keeps the standard header
                      // height; align to match the column.
                      <span className={`flex min-h-[2.5rem] items-center px-4 py-2 ${headerJustify[col.align ?? 'start']}`}>
                        {col.header}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {segments
              ? // ONE flat, keyed list of rows (a nested array per run would be an unkeyed fragment React matches by
                // index — every shift of the segments would remount the rows that moved and drop the focus in them).
                segments.flatMap((seg, i) => {
                  const afterBand = i > 0 && segments[i - 1]!.kind === 'band';
                  return seg.kind === 'band' ? [serverBandRow(seg, afterBand)] : rows.slice(seg.from, seg.to).map((row, j) => leafRow(row, j === 0 && afterBand));
                })
              : rows.map((row, rowIdx) => {
                  // The band's bottom hairline already draws the boundary line — a row (leaf or next band) directly
                  // under a band drops its own border-t, so every divider is exactly 1px (never a 2px stack).
                  const afterBand = rowIdx > 0 && rows[rowIdx - 1]!.getIsGrouped();
                  if (!row.getIsGrouped()) return leafRow(row, afterBand);
                  // Deeper band levels ride INLINE (indented, lighter, no pin) — only the level-0 band pins under the thead.
                  return bandRow({
                    id: row.id,
                    expanded: row.getIsExpanded(),
                    onToggle: row.getToggleExpandedHandler(),
                    nested: row.depth > 0,
                    afterBand,
                    content: groupHeader
                      ? groupHeader(
                          String(row.groupingValue ?? ''),
                          // TRUE leaves only — getLeafRows() flattens descendant GROUP rows too, which would inflate
                          // band counts on multi-level tables.
                          row
                            .getLeafRows()
                            .filter((r) => !r.getIsGrouped())
                            .map((r) => r.original),
                          row.groupingColumnId,
                        )
                      : String(row.groupingValue ?? ''),
                  });
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
